/** Черновики ответов ИИ в «Чатах» — `/api/chats/{id}/suggest`. Клиенту уходит только кнопкой. */
import { apiFetch } from "@/lib/api";
import type { Podbor } from "@/lib/podbor";
import type { LabChecklist } from "@/lib/voiceLab";

export type { LabChecklist };

/** Действие в CRM, которое модель предлагает вместе с ответом: создать сделку, поставить стадию
 * (или записать на названный день), дату следующей обработки, закрыть сделку отказом с причиной,
 * переслать сообщение стилисту в группу салона. Выполняется по «Отправить» от имени нажавшего, раньше текста. */
export type SuggestAction = {
  kind: "deal_create" | "deal_stage" | "deal_wait" | "deal_refuse" | "salon_forward";
  /** Действие одной строкой: «Создать сделку — стадия «В работе», Краснодар». */
  title: string;
  /** Уже выполнено (текст после этого не ушёл — черновик ждёт повторного «Отправить»). */
  done: boolean;
  /** Почему действие больше не нужно (сделку уже завели руками); пусто — будет выполнено. */
  skip: string;
  /** Сделка действия: созданная или та, которую правим. */
  deal_id: number | null;
};

export type ChatSuggest = {
  id: number;
  /** На какое сообщение клиента написан черновик. */
  message_id: number;
  state: "new" | "sending" | "sent" | "edited" | "rejected" | "stale" | "skip" | "failed";
  /** Текст клиенту: ссылка личного кабинета и адрес салона уже подставлены. */
  text: string;
  /** Пояснение модели: почему ответ такой. */
  why: string;
  /** Город, который назвал клиент, — чат к нему уже привязан. */
  city: string | null;
  date: string | null;
  comment: string;
  user: string;
  /** Сообщение клиента, на которое отвечает черновик (есть у ждущего решения). */
  question?: string;
  /** Запрос на подбор в группу салона: уйдёт туда вместе с этим ответом по кнопке «Отправить». */
  podbor?: Podbor | null;
  /** Действия в CRM: уйдут в Bitrix вместе с этим ответом по кнопке «Отправить». */
  actions?: SuggestAction[];
  /** Кнопка AI: линия сценария «chat/zapis/v1:хэш» или «chat/suggest_instruction.md»; пусто — фоновый черновик. */
  label?: string;
  /** Комната черновика в AI Lab — туда уходит замечание («Отказать»). */
  room?: string | null;
  /** Тип обращения, по которому написан черновик кнопки AI. */
  scenario?: { type: AiType; why: string; confidence: number | null; manual: boolean } | null;
  /** Цена, USD: сам черновик и детектор типа (кнопка AI). */
  cost?: number | null;
  detect_cost?: number | null;
  /** Модель черновика. */
  model?: string;
};

/** Хронология кнопки AI в чате: черновики свежие сверху, к каждому — замечания AI Lab с разбором. */
export type AiHistoryDraft = ChatSuggest & {
  at: string | null;
  decided_at: string | null;
  state_label: string;
  question: string;
  /** Почему черновик не вышел (модель не ответила, ответ не по форме). */
  error: string;
  total_cost: number;
  scenario: { type: AiType; label: string; why: string; confidence: number | null; manual: boolean } | null;
  remarks: {
    id: number;
    status: string;
    status_label: string;
    text: string;
    transcript: string;
    answer: { change?: string; version?: string; cause?: string } | null;
  }[];
};
export type AiHistory = {
  drafts: AiHistoryDraft[];
  count: number;
  cost: number;
  rate: number;
  rate_source: string;
  /** последний чек-лист обращения чата (`since` — начало его обращения); null — нет */
  checklist?: LabChecklist | null;
};
export const aiHistoryQuery = (chatId: number) => ({
  queryKey: ["chat-ai-history", chatId],
  queryFn: () => apiFetch<AiHistory>(`/api/chats/${chatId}/suggest/history`),
});
/** «$0.033 · ≈ 3 ₽». */
export const money = (usd: number, rate?: number) =>
  `$${usd.toFixed(3)}${rate ? ` · ≈ ${(usd * rate).toFixed(usd * rate < 10 ? 1 : 0)} ₽` : ""}`;

/** Типы обращения — общие для каналов (`voicelab/tools.ТЕМЫ`). Свои линии сценария — у zapis и oplata. */
export const AI_TYPES = {
  zapis: "Прокат: подобрать и записать",
  sdat: "Сдать или продать своё платье",
  bron: "Оформленная бронь",
  oplata: "Оплата брони",
  zhaloba: "Жалоба",
  drugoe: "Другое, не клиент",
} as const;
export type AiType = keyof typeof AI_TYPES;

/** «chat/zapis/v1:3c8d42f7» → «chat/zapis v1 · 3c8d42f7»; общая инструкция — словами. */
export function lineLabel(label: string): string {
  if (!label.includes("/v")) return "Общая инструкция «Чатов» · своей линии у типа нет";
  const [head = "", hash] = label.split(":");
  const i = head.lastIndexOf("/");
  return `${head.slice(0, i)} ${head.slice(i + 1)}${hash ? ` · ${hash}` : ""}`;
}
type Answer = { suggest: ChatSuggest };

/** Черновик, ждущий решения; `null` — нет (не готов, устарел, модель оставила ответ человеку). */
export const loadSuggest = (chatId: number) => apiFetch<{ suggest: ChatSuggest | null }>(`/api/chats/${chatId}/suggest`);

/** Кнопка AI: тип обращения (детектор или выбранный `type`) и черновик по сценарию этого типа. */
export const aiSuggest = (chatId: number, type?: AiType) =>
  apiFetch<{ suggest: ChatSuggest | null }>(`/api/chats/${chatId}/suggest`, {
    method: "POST",
    body: JSON.stringify(type ? { type } : {}),
  });

const decide = (chatId: number, id: number, what: "accept" | "edit" | "reject", body?: object) =>
  apiFetch<Answer>(`/api/chats/${chatId}/suggest/${id}/${what}`, { method: "POST", body: JSON.stringify(body ?? {}) });

/** «Отправить»: черновик уходит клиенту как есть; перед этим выполняются действия в CRM —
 * `actions` перечисляет виды, у которых сотрудник оставил галочку (пусто — только текст). */
export const acceptSuggest = (chatId: number, id: number, actions?: SuggestAction["kind"][]) =>
  decide(chatId, id, "accept", actions ? { actions } : undefined);
/** «В поле ответа»: текст — в поле, сотрудник поправит и отправит сам. */
export const editSuggest = (chatId: number, id: number) => decide(chatId, id, "edit");
/** «Отклонить» с замечанием — по замечаниям правится инструкция модели. */
export const rejectSuggest = (chatId: number, id: number, comment: string) => decide(chatId, id, "reject", { comment });
